/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment */

import type BuildingDisplay from '@hoverinc/machete-core/build/display/index';
import type { HistoryMutation } from '@hoverinc/machete-core/build/feature-api/PublicInterface';
import type { AppearancePreset } from '@hoverinc/machete-core/build/feature-api/feature/AppearancePreset';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { SavedDesign } from 'src/modules/designStudio/providers/SavedDesignsProvider/SavedDesign';
import { createBuildingAppearance } from 'src/modules/designStudio/providers/SavedDesignsProvider/utils';

export type AppearanceGroup = { appearance: AppearancePreset; uuids: string[] };
/**
 * By storing the uuid instead of the feature, the client has to make sure to query for the
 * feature, preventing a old feature from being stored and becoming stale.
 */
export type BuildingFeatureAppearances = {
  walls: AppearanceGroup[];
  roofs: AppearanceGroup[];
  trims: AppearanceGroup[];
  windows: AppearanceGroup[];
  doors: {
    front: AppearanceGroup[];
    service: AppearanceGroup[];
    garage: AppearanceGroup[];
  };
};

type ContextValue = {
  /**
   * The currently active design index, at load time can be -1.
   */
  activeDesignIndex: number;
  /**
   * List of all the designs associated with the current model.
   */
  savedDesigns: SavedDesign[];

  /**
   * Indicates when the user has unsaved changes. This is used
   * to notify the user if they are going to close the application and lose
   * data. This flag is only correctly set if changes made to the model
   * are done through mutateBuilding().
   */
  hasUnsavedChanges: boolean;

  /**
   * This stores the appearance of the building, clients should use this
   * as the source of truth of what the building looks like. This property is re-calulated
   * whenever the building appearance is changed via mutateBuilding()
   */
  buildingAppearances: BuildingFeatureAppearances;

  /**
   * This is the one and only way that a user should make any modifications
   * to a building. This function takes as argument a mutation, which is a function
   * that modifies the state of the building. For example:
   *
   * // user clicks to apply a wall material
   * const materialId = getUserSelection();
   *
   * const designs = useDesigns();
   *
   * designs.mutateBuilding( ({walls}) => {
   *   walls.forEach(w => w.appearance_preset = createTexturePreset(materialId))
   *  }
   * );
   *
   * Having all changes go through this one method is useful cause it allows
   * us to keep track of when the suer has unsaved changes.
   */
  mutateBuilding: (mutation: HistoryMutation) => void;

  /**
   * Indicates when a design is being loaded. used to control
   * spinner that shows user load progress.
   */
  isDesignLoading: boolean;

  /**
   * Loads a saved design. This handles passing the saved design's
   * HDF to machete to update the rendered 3D model. Also flips the isDesignLoading
   * flag to control the loading animation.
   */
  changeActiveDesign: (design: SavedDesign) => void;

  /**
   * Indicates when a design is being saved. Used to control
   * spinner that shows user progress.
   */
  isDesignSaving: boolean;

  /**
   * Saves and overwrites existing saveActiveDesign. This will update
   * the thumbnail and hdf associated with the design.
   */
  saveActiveDesign: () => void;

  /**
   * Saves existing design as a new named design.
   * - Invokes QL mutation to saveActiveDesign design
   * - Updates activeDesign to new saveActiveDesign
   */
  saveAsNewDesign: (name: string) => void;

  /**
   * Deletes the currently active design.
   * - Invoked QL mutation to delete design on BE
   * - Finds and loads original design
   * - Flips isDesignLoading flag to control saving animation
   */
  deleteActiveDesign: () => void;
  /**
   * Updates the name of the currently active design. The user
   * is only able to modify the name of the currently active design so this function
   * does not take as argument the design we want to update.
   */
  updateActiveDesignName: (name: string) => void;

  undo: () => void;
  redo: () => void;

  get canUndo(): boolean;
  get canRedo(): boolean;
};

const mockSavedDesign: SavedDesign = {
  uuid: '',
  isOriginal: true,
  creationDate: '',
  lastOpenedDate: '',
  lastModifiedDate: '',
  name: 'Hover Lane',
  thumbnailURL: 'https://gifs.intercomcdn.com/l6Ok5ZYPfjwAAAAC/please-cat.gif',
  hdf: {},
};

export const defaultContextValue: ContextValue = {
  // activeDesignIndex: -1,
  // isDesignLoading: true,
  // savedDesigns: [],

  activeDesignIndex: 0,
  isDesignLoading: false,
  savedDesigns: [mockSavedDesign],

  hasUnsavedChanges: false,
  buildingAppearances: createBuildingAppearance(undefined),
  mutateBuilding: () => {},
  changeActiveDesign: () => {},
  isDesignSaving: false,
  updateActiveDesignName: () => {},
  saveActiveDesign: () => {},
  saveAsNewDesign: () => {},
  deleteActiveDesign: () => {},
  undo: () => {},
  redo: () => {},
  get canRedo() {
    return false;
  },
  get canUndo() {
    return false;
  },
};

const context = createContext<ContextValue>(defaultContextValue);

/**
 * At load time:
 *
 * Make query to fetch all saved designs associated with modelId.
 * Pick one of these designs to load
 *  - pick most recently created
 *  - pick most recently opened (we would log each time a user opens a design)
 * Store saved designs in state
 *  - is this necessary since graphQL quriries handle caching results?
 *  - instead of simply storing the network result we can store in memory data structure
 *    - part of creating data structure is fetching the image data associated with each design
 *      - we have a decision on lazy loading vs eager loading
 *      - we probably want to do eager loading since most models will have a small amount of saved designs
 *      - eager loading -> lazy loading is easier than lazy -> eager
 * Load from saved HDF
 *  - at first load we will have no problems, one second load we will probably
 *  - face issues related to not cleaning up between loads correctly.
 *
 * When the user opens their saved designs menu:
 * - will utilize in memory data structures to populate UI
 *
 * When the user clicks on a saved design
 * - log to server that user selected a design(used to update lastOpened field)
 * - HDF should already be present to simply pass hdf to loadHDF method
 *
 * When the user makes a change to the model through one of the menus
 * - it will be using the feature api,
 * - we will keep track of the fact that the user has made a change and can saveActiveDesign
 *  - this will make the saveActiveDesign button active/present
 *  - this will also allow us to show the unsaved chnages modal if user tries to leave.
 *
 * When the user clicks saveActiveDesign
 * - we will ask machete to write out HDF, this will take a second or two
 * - we will rotate the model and take a screenshot of the canvas
 * - once the image and HDF are written out we can then make the mutation to saveActiveDesign
 * - the saving progress should only finish once the mutation is complete
 * - we can optimize this by writing out only the geometry & appearance
 *    - if the user has not modified geometry we can also extra optimize this
 *    - optimization inside machete, keep track of whether a split has been made
 *      - for rn only templates will affect geometry so for most cases where the user simply chnages
 *      texture this write process could be much faster
 *      - we will want to keep track of how long the writing and saving takes to
 *      show a progress scroll thingy
 *
 * When the user saves we need to handle updating our in memory data structures
 * - Probably we should just make a new query and completly re-create the in memory data structures.
 *  - In this case a problem with hving objects is that someone down the line could store
 *  - this object and then it would be out of date.
 *  - Perhaps because of this we should take a purely functional approach, or
 *
 * We need to rehydrate our in memory store when
 * - saveActiveDesign new design
 * - overwrite existing saved design
 * - rename design
 * - delete saved design
 *
 * Deleting saved design
 * - will require making a mutation to delete
 * - rehydrate or simply delete that one design?
 * - and also will cause us to load the original design.
 *
 *
 * Network Optimizations:
 * - we have two options
 *  - whenever the user makes changes we re-hydrate completly and re-create data structures
 *  - we only rehydrate when absolutely needed and otherwise simply update in memory data structures
 *
 *
 *  We will utilize the Apollo GraphQL cache to optimize network utilization
 *  and responsiveness.
 *
 *  How will UI components interact with data
 *
 *  Designs Menu: [shows list of saved designs, allows user to load and create saved design]
 *  - need list of all designs
 *    - will use following fields
 *      - name & thumbnail to create card
 *      - creationDate/modifiedDate to sort designs
 *      - number of designs to show count
 *  - needs to know which design is currently active to populate check button
 *  - needs to know which design is original
 *
 *  - when the user clicks on a saved design
 *    - we need to pass the hdf file to machete helper function changeActiveDesign
 *    - close menu and replace canvas with loading animated div
 *  - loading animation
 *    when the changeActiveDesign method is called the context state variable
 *    isDesignLoading will be set to true. This variable will be used
 *    to determine whether loading div or canvas is rendered in higher up component.
 *
 *  -
 */
const Provider = ({ children }: { children: React.ReactNode }) => {
  // const { building } = useContext(MacheteBuildingProvider);

  /* GraphQL Query and Mutations
  Subject to change.
  const getDesigns = useQuery();
  const [saveDesign, saveDesignResult] = useMutation();
  const [updateDesignName, updateDesignNameResult] = useMutation();
  const [saveAsNewDesign, saveAsNewDesignResult] = useMutation();
  const [deleteDesign, deleteDesignResult] = useMutation(); */

  const getDesigns: {
    data: {
      designs: SavedDesign[];
    };
  } = {
    data: {
      designs: [mockSavedDesign],
    },
  };

  const [display] = useState<BuildingDisplay | undefined>(undefined);
  const [designs, setDesigns] = useState<SavedDesign[]>([]);
  const [hasInitialized, setHasInitialized] = useState(true);
  const [activeDesignIndex, setActiveDesignIndex] = useState<number>(-1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDesignLoading, setIsDesignLoading] = useState(true);
  const [isDesignSaving, setIsDesignSaving] = useState(false);

  /* eslint-disable @typescript-eslint/require-await */

  /**
   * @description Invokes QL mutations to saveActiveDesign design, along with updating state.
   */
  const onSaveDesign = useCallback(
    // @ts-ignore
    async (name: string, doSaveAsNew: boolean): Promise<SavedDesign> => {
      setHasUnsavedChanges(false);
      setIsDesignSaving(true);

      /*
      const variables = {
        thumbnail: await captureModelThumbnail(),
        hdf: {}, // await writeHDF(building),
      }; */

      if (doSaveAsNew) {
        /* void saveAsNewDesign({
          variables: {
            ...variables,
            name,
          },
        }); */
      } else {
        /* void saveDesign({
          variables,
        }); */
      }
      return mockSavedDesign;
    },
    [],
  );

  const contextValue = useMemo(() => {
    const { designs: qlDesigns } = getDesigns.data as {
      designs: SavedDesign[];
    };

    /**
     * Do more sophisticated check to see if we need to update local state.
     */
    if (qlDesigns.length !== designs.length) {
      setDesigns(qlDesigns);
    }

    return {
      activeDesignIndex,
      savedDesigns: designs,
      hasUnsavedChanges,

      buildingAppearances: createBuildingAppearance(undefined),

      mutateBuilding: (mutation: HistoryMutation) => {
        display?.history.pushDiff(mutation);
        /**
         * TODO: Regenerate buildingAppearances once we have an actual instanced buildingDisplay.
         */
        setHasUnsavedChanges(true);
      },

      isDesignLoading,

      changeActiveDesign: async (_: SavedDesign) => {
        setIsDesignLoading(true);
        // const { hdf } = design;
        /**
         * Pass HDF to machete to load.
         */
        setIsDesignLoading(false);
      },

      isDesignSaving,
      saveActiveDesign: async () => {
        const design = designs[activeDesignIndex];
        if (design) {
          await onSaveDesign(design.name, false);
        }
      },
      saveAsNewDesign: async (name: string) => {
        const design = await onSaveDesign(name, true);
        const n = designs.length;
        setDesigns([...designs, design]);
        setActiveDesignIndex(n);
      },

      deleteActiveDesign: async () => {
        // void deleteDesign({ variables: { id: activeDesign!.uuid } });
        // const original = designs.find(d => d.isOriginal) as SavedDesign;
        setIsDesignLoading(true);
        // load design
        setIsDesignLoading(false);
        setDesigns(designs.splice(activeDesignIndex, 1));
      },
      // @ts-ignore
      updateActiveDesignName: async (name: string) => {
        /* await updateDesignName({
          variables: {
            id: activeDesign?.uuid,
            name,
          },
        }); */
        const arr = [...designs];
        arr[activeDesignIndex].name = name;
        setDesigns(arr);
      },

      undo: () => display?.history.undo(),
      redo: () => display?.history.redo(),

      get canUndo() {
        return display?.history.canUndo ?? false;
      },

      get canRedo() {
        return display?.history.canRedo ?? false;
      },
    } as ContextValue;
  }, [
    activeDesignIndex,
    designs,
    getDesigns.data,
    hasUnsavedChanges,
    isDesignLoading,
    isDesignSaving,
    onSaveDesign,
    setDesigns,
    setActiveDesignIndex,
    setHasUnsavedChanges,
    setIsDesignLoading,
    display,
  ]);
  /* eslint-enable @typescript-eslint/require-await */

  /**
   * At initial load time this will handle loading the most recently opened design.
   */
  useEffect(() => {
    if (!hasInitialized && getDesigns.data) {
      void contextValue.changeActiveDesign(getDesigns.data.designs[0]);
      setHasInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialized]);

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export { Provider, context };
